// Exercise database
const EXERCISES = {
  strength: [
    {
      id: 'chair-squats',
      name: 'Chair Squats',
      description: 'Sit and stand from a chair to build leg strength',
      difficulty: 1,
      sets: 3,
      reps: 10,
      restSeconds: 60,
      focusAreas: ['Legs', 'Core'],
      modifications: ['Use armrests', 'Add cushion']
    },
    {
      id: 'wall-pushups',
      name: 'Wall Push-ups',
      description: 'Push-ups against a wall to build upper body strength',
      difficulty: 1,
      sets: 3,
      reps: 12,
      restSeconds: 45,
      focusAreas: ['Chest', 'Arms'],
      modifications: ['Stand closer to wall', 'Use counter']
    },
    {
      id: 'arm-circles',
      name: 'Arm Circles',
      description: 'Circular motions with arms to improve shoulder mobility',
      difficulty: 1,
      sets: 2,
      reps: 15,
      restSeconds: 30,
      focusAreas: ['Shoulders', 'Arms'],
      modifications: ['Smaller circles', 'Seated position']
    }
  ],
  balance: [
    {
      id: 'heel-toe-walk',
      name: 'Heel-to-Toe Walk',
      description: 'Walk in a straight line with heel touching toe',
      difficulty: 2,
      sets: 2,
      reps: 10,
      restSeconds: 45,
      focusAreas: ['Balance', 'Coordination'],
      modifications: ['Use wall support', 'Wider stance']
    },
    {
      id: 'single-leg-stand',
      name: 'Single Leg Stand',
      description: 'Stand on one leg while holding onto support',
      difficulty: 2,
      sets: 3,
      reps: 30,
      restSeconds: 30,
      focusAreas: ['Balance', 'Core'],
      modifications: ['Use chair support', 'Shorter duration']
    }
  ],
  flexibility: [
    {
      id: 'shoulder-stretch',
      name: 'Shoulder Stretch',
      description: 'Cross arm across chest to stretch shoulder',
      difficulty: 1,
      sets: 2,
      reps: 30,
      restSeconds: 30,
      focusAreas: ['Shoulders', 'Upper Back'],
      modifications: ['Gentle stretch', 'Seated position']
    },
    {
      id: 'ankle-rotations',
      name: 'Ankle Rotations',
      description: 'Rotate ankles to improve mobility',
      difficulty: 1,
      sets: 2,
      reps: 20,
      restSeconds: 30,
      focusAreas: ['Ankles', 'Lower Legs'],
      modifications: ['Seated position', 'Smaller range']
    }
  ]
};

export const getRecommendation = async (userProfile) => {
  try {
    // Validate input data
    if (!userProfile) {
      throw new Error('User profile is required');
    }

    // Simple profile analysis
    const userFactors = {
      fitnessLevel: userProfile.fitnessLevel || 'beginner',
      goals: userProfile.goals || [],
      healthConditions: userProfile.healthConditions || [],
      equipment: userProfile.equipment || [],
      hasLimitations: (userProfile.healthConditions || []).length > 0
    };

    // Create a simple workout based on user's fitness level and goals
    const workout = {
      name: 'Personalized Workout',
      type: 'strength',
      exercises: [],
      estimatedDuration: 30,
      focusAreas: []
    };

    // Add exercises based on fitness level
    const difficulty = userFactors.fitnessLevel === 'beginner' ? 1 : 
                      userFactors.fitnessLevel === 'intermediate' ? 2 : 3;

    // Select exercises based on goals
    const selectedExercises = [];
    
    // Always include at least one strength exercise
    const strengthExercises = EXERCISES.strength.filter(ex => ex.difficulty <= difficulty);
    if (strengthExercises.length > 0) {
      selectedExercises.push(strengthExercises[0]);
    }

    // Add balance exercises if balance is a goal
    if (userFactors.goals.includes('Better Balance')) {
      const balanceExercises = EXERCISES.balance.filter(ex => ex.difficulty <= difficulty);
      if (balanceExercises.length > 0) {
        selectedExercises.push(balanceExercises[0]);
      }
    }

    // Add flexibility exercises
    const flexibilityExercises = EXERCISES.flexibility.filter(ex => ex.difficulty <= difficulty);
    if (flexibilityExercises.length > 0) {
      selectedExercises.push(flexibilityExercises[0]);
    }

    // Update workout with selected exercises
    workout.exercises = selectedExercises;
    workout.focusAreas = [...new Set(selectedExercises.flatMap(ex => ex.focusAreas))];

    return workout;
  } catch (error) {
    console.error('Error generating workout recommendation:', error);
    throw error;
  }
};

export const getWorkoutRecommendation = async (userId) => {
  try {
    // Return a proper exercise-based workout for AI generation
    const workout = {
      id: `ai-workout-${Date.now()}`,
      name: 'AI Generated Workout',
      exercises: [
        {
          id: 'ai-chair-squats',
          name: 'Chair Squats',
          description: 'Sit and stand from a chair to build leg strength',
          sets: 3,
          reps: 10,
          restSeconds: 60,
          focusAreas: ['Legs', 'Core']
        },
        {
          id: 'ai-wall-pushups',
          name: 'Wall Push-ups',
          description: 'Push-ups against a wall to build upper body strength',
          sets: 3,
          reps: 12,
          restSeconds: 45,
          focusAreas: ['Chest', 'Arms']
        },
        {
          id: 'ai-arm-circles',
          name: 'Arm Circles',
          description: 'Circular motions with arms to improve shoulder mobility',
          sets: 2,
          reps: 15,
          restSeconds: 30,
          focusAreas: ['Shoulders', 'Arms']
        },
        {
          id: 'ai-single-leg-stand',
          name: 'Single Leg Stand',
          description: 'Stand on one leg while holding onto support for balance',
          sets: 3,
          reps: 30,
          restSeconds: 30,
          focusAreas: ['Balance', 'Core']
        },
        {
          id: 'ai-shoulder-stretch',
          name: 'Shoulder Stretch',
          description: 'Cross arm across chest to stretch shoulder',
          sets: 2,
          reps: 30,
          restSeconds: 30,
          focusAreas: ['Shoulders', 'Upper Back']
        }
      ],
      focusAreas: ['Strength', 'Balance', 'Flexibility'],
      estimatedDuration: 25
    };
    
    return workout;
  } catch (error) {
    console.error('Error getting workout recommendation:', error);
    throw new Error('Failed to get workout recommendation');
  }
}; 